// De Dagstart als rooster: personen als rijen, dagen als kolommen — hetzelfde RoosterGrid als
// Planning → Rooster, gevoed met de Dagstart-data (geen tweede ophaal- of agenda-ronde).
// Slepen verzet een taak via de schrijfkant van de Dagstart (optimistisch, met ongedaan).
// Klik op een naam = de gedetailleerde per-persoon-weergave eronder (zie Dagstart.jsx).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import RoosterGrid from '../PlannerTodo/RoosterGrid';
import { botsing } from '../PlannerTodo/useRooster';
import { verzettingUitDrop, blokMelding } from '../PlannerTodo/verzetten';
import {
  bouwDagstartRooster, groepeerOpDiscipline, kolommenTussen, kolomSjabloon, wekenVoorBreedte,
} from './roosterUitDagstart';

const GEEN_ONBEKEND = {};

/**
 * @param rijen            zichtbare Dagstart-rijen (teamtab + zoekterm al toegepast)
 * @param takenPerNaam     taken per rij (zonder verborgen privé-taken)
 * @param agendaUren       agenda-uren per rij
 * @param disciplinePerNaam  { naam: disciplineKey }
 * @param start / eind     het venster (ISO)
 * @param werkdagen        5, 10 of 20
 * @param agendaFout       agenda niet geladen (melding bovenin het rooster)
 * @param detailNaam       persoon waarvan de detailweergave open is (gemarkeerd)
 * @param onNaamKlik       (naam|null) => void
 * @param onTaakOpen       taak => void (TaakPaneel van de Dagstart)
 * @param onVerzet         Dagstart-verzet({ taak, soort, startDate, endDate })
 * @param werkGroepBij     Dagstart-schrijfkant voor een heel projectblok: één actie, één ongedaan-stap
 * @param horizonPerNaam / horizonDagen  rekenhorizon voorbij het venster (zoals PersonTimeline)
 * @param onBezig          (bool) => void — gebaarbewaking: geen achtergrondronde tijdens slepen
 */
function DagstartRooster({
  rijen, takenPerNaam, agendaUren, disciplinePerNaam, start, eind, werkdagen, rules, agendaFout,
  detailNaam, onNaamKlik, onTaakOpen, onVerzet, werkGroepBij, kopLinks, sx,
  horizonPerNaam, horizonDagen, onBezig, verbergKlein = false, eenPersoon = false,
}) {
  const kolommen = useMemo(() => kolommenTussen(start, eind), [start, eind]);
  const sjabloon = useMemo(() => kolomSjabloon(kolommen, wekenVoorBreedte(werkdagen)), [kolommen, werkdagen]);
  const data = useMemo(
    () => bouwDagstartRooster({
      rijen, takenPerNaam, agendaUren, kolommen, rules, horizonPerNaam, horizonDagen, verbergKlein,
      zonderAgendaBlokken: eenPersoon,
    }),
    [rijen, takenPerNaam, agendaUren, kolommen, rules, horizonPerNaam, horizonDagen, verbergKlein, eenPersoon],
  );
  const groepen = useMemo(() => groepeerOpDiscipline(data.persons, disciplinePerNaam), [data.persons, disciplinePerNaam]);

  const [activeTask, setActiveTask] = useState(null);
  const [overCell, setOverCell] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  /** Heel projectblok evenveel werkdagen verschuiven; één melding, één stap ongedaan (ook Cmd+Z). */
  const verschuifBlok = useCallback(async ({ taken, delta }) => {
    const items = taken.map((x) => ({
      taskId: x.taak.id,
      velden: { StartDateUitvoerder: x.startDate, EndDateUitvoerder: x.endDate },
      terug: { StartDateUitvoerder: x.vorigeStart, EndDateUitvoerder: x.vorigeEind },
    }));
    await werkGroepBij(items, { melding: blokMelding(items.length, delta) });
  }, [werkGroepBij]);

  // Gebaarbewaking: tijdens een sleep mag de minuutronde geen blokken vervangen. Precies één
  // aan/uit per gebaar, ook als einde en annuleren allebei komen.
  const sleeptRef = useRef(false);
  const meld = useCallback((bezig) => {
    if (sleeptRef.current === bezig) return;
    sleeptRef.current = bezig;
    onBezig?.(bezig);
  }, [onBezig]);
  useEffect(() => () => { if (sleeptRef.current) onBezig?.(false); }, [onBezig]);

  const dnd = useMemo(() => ({
    sensors,
    collisionDetection: botsing,
    onDragStart: (event) => {
      meld(true);
      const d = event.active.data.current;
      if (d?.type === 'task-bar') setActiveTask(d.task);
      else if (d?.type === 'blok') setActiveTask({ ...d.tasks[0], name: `${d.label || 'Project'} — ${d.tasks.length} taken` });
    },
    onDragOver: (event) => {
      const o = event.over?.data?.current;
      setOverCell(o?.type === 'capacity-cell' ? { personId: o.personId, date: o.date } : null);
    },
    onDragCancel: () => { setActiveTask(null); setOverCell(null); meld(false); },
    onDragEnd: async (event) => {
      setActiveTask(null);
      setOverCell(null);
      meld(false);
      // In de Dagstart verzet je in de tijd; een taak aan een collega geven gaat via het
      // taakpaneel ("Toegewezen") of het Rooster, waar de mede-uitvoerders bewaakt worden.
      const v = verzettingUitDrop(event.active?.data?.current, event.over?.data?.current);
      if (!v) return;
      if (v.soort === 'collega') {
        toast('Naar een collega verplaatsen: open de taak en kies bij Toegewezen', { icon: '👤' });
        return;
      }
      if (v.soort === 'blok') { await verschuifBlok(v); return; }
      await onVerzet({ taak: v.taak, soort: 'verplaats', startDate: v.startDate, endDate: v.endDate });
    },
  }), [sensors, verschuifBlok, onVerzet, meld]);

  const rooster = useMemo(() => ({
    ...data,
    kolommen, sjabloon, rules,
    agendaLaden: false,
    agendaFout: !!agendaFout,
    agendaOnbekend: GEEN_ONBEKEND,
    overCell, activeTask, dnd, speeltuin: null,
  }), [data, kolommen, sjabloon, rules, agendaFout, overCell, activeTask, dnd]);

  const kiesNaam = useCallback((id) => onNaamKlik(id), [onNaamKlik]);

  return (
    <RoosterGrid
      rooster={rooster}
      groepen={groepen}
      selectedPerson={detailNaam}
      onSelectPerson={kiesNaam}
      onTaakOpen={onTaakOpen}
      naamHint="Klik voor de dag van deze persoon met tijden (eronder)"
      kopLinks={kopLinks}
      sx={sx}
      // Joris 6 okt: meer lucht tussen personen, dagtotaal ook als ruimte ("+3u").
      ruim
      toonRuimte
      // Joris 7 okt: kleur = soort werk (legenda bovenaan), ruitje aan het einde van elke taak.
      betekenis
      eenPersoon={eenPersoon}
    />
  );
}

export default React.memo(DagstartRooster);
